/**
 * The permission engine.
 *
 * This is the single source of truth for "who may do what". It is a pure
 * function — no database, no network, no clock — so it is exhaustively
 * unit-testable and runs identically on the server, in the browser (to grey out
 * buttons) and in React Native.
 *
 * The server treats this as authoritative. The client treats it as a hint:
 * every mutation is re-checked server-side against freshly loaded state.
 *
 * Resolution order (each step can only narrow, except explicit grants):
 *
 *   role base set
 *     → group policy gates      (can only REMOVE, and only from members)
 *       → membership overrides  (deny, then grant; grants are clamped)
 *         → group status clamp  (locked ⇒ read-only)
 */

export const CAPABILITIES = [
  // Content
  "file:view",
  "file:upload",
  "file:download",
  "file:download_original",
  "file:delete_own",
  "file:delete_any",
  "file:restore",
  "file:organize", // move between folders, set cover, edit captions
  "folder:create",
  "folder:manage",
  // Social
  "post:create",
  "post:delete_own",
  "post:delete_any",
  "comment:create",
  "comment:delete_own",
  "comment:delete_any",
  "reaction:create",
  // Sharing
  "share:create",
  "share:manage",
  // People
  "member:invite",
  "member:remove",
  "member:manage_roles",
  // Group
  "group:manage_settings",
  "group:manage_appearance",
  "group:delete",
  "audit:view",
] as const;

export type Capability = (typeof CAPABILITIES)[number];

export const GROUP_ROLES = ["owner", "admin", "member"] as const;
export type GroupRole = (typeof GROUP_ROLES)[number];

export const GROUP_STATUSES = ["active", "locked", "deleting", "deleted"] as const;
export type GroupStatus = (typeof GROUP_STATUSES)[number];

/**
 * The nine toggles from §35, plus `allowOriginalDownloads`.
 *
 * These gate MEMBERS ONLY. Turning off "members can download" does not stop an
 * admin from downloading — that would make the toggle a footgun rather than a
 * policy.
 */
export interface GroupSettings {
  membersCanUpload: boolean;
  membersCanCreateTrips: boolean;
  membersCanDeleteOwnFiles: boolean;
  membersCanDeleteOthersFiles: boolean;
  membersCanDownload: boolean;
  membersCanShareExternally: boolean;
  membersCanCreatePosts: boolean;
  membersCanComment: boolean;
  membersCanInvite: boolean;
  /** Applies to every role: when false, nobody pulls originals over the wire. */
  allowOriginalDownloads: boolean;
}

/** Defaults exactly as specified in §35. */
export const DEFAULT_GROUP_SETTINGS: GroupSettings = {
  membersCanUpload: true,
  membersCanCreateTrips: true,
  membersCanDeleteOwnFiles: true,
  membersCanDeleteOthersFiles: false,
  membersCanDownload: true,
  membersCanShareExternally: false,
  membersCanCreatePosts: true,
  membersCanComment: true,
  membersCanInvite: false,
  allowOriginalDownloads: true,
};

export interface PermissionOverrides {
  grant?: Capability[];
  deny?: Capability[];
}

const MEMBER_CAPABILITIES: readonly Capability[] = [
  "file:view",
  "file:upload",
  "file:download",
  "file:download_original",
  "file:delete_own",
  "file:organize",
  "folder:create",
  "post:create",
  "post:delete_own",
  "comment:create",
  "comment:delete_own",
  "reaction:create",
];

const ADMIN_ONLY_CAPABILITIES: readonly Capability[] = [
  "file:delete_any",
  "file:restore",
  "folder:manage",
  "post:delete_any",
  "comment:delete_any",
  "share:create",
  "share:manage",
  "member:invite",
  "member:remove",
  "member:manage_roles",
  "group:manage_settings",
  "group:manage_appearance",
  "audit:view",
];

const OWNER_ONLY_CAPABILITIES: readonly Capability[] = ["group:delete"];

export const ROLE_CAPABILITIES: Record<GroupRole, readonly Capability[]> = {
  member: MEMBER_CAPABILITIES,
  admin: [...MEMBER_CAPABILITIES, ...ADMIN_ONLY_CAPABILITIES],
  owner: [...MEMBER_CAPABILITIES, ...ADMIN_ONLY_CAPABILITIES, ...OWNER_ONLY_CAPABILITIES],
};

/**
 * Capabilities that a per-member override may never grant, no matter who sets
 * it. Group deletion must always require the owner role plus TOTP, and quota is
 * platform-level only — see `PlatformCapability`.
 */
const NON_GRANTABLE: readonly Capability[] = ["group:delete"];

/** What survives when a group is locked by the platform admin. */
const READ_ONLY_CAPABILITIES: readonly Capability[] = [
  "file:view",
  "file:download",
  "file:download_original",
];

/**
 * Which group setting gates each member capability. These settings can only
 * REMOVE a capability the member's role already grants.
 */
const SETTING_GATES: Partial<Record<Capability, keyof GroupSettings>> = {
  "file:upload": "membersCanUpload",
  "file:download": "membersCanDownload",
  "file:delete_own": "membersCanDeleteOwnFiles",
  "folder:create": "membersCanCreateTrips",
  "post:create": "membersCanCreatePosts",
  "comment:create": "membersCanComment",
  "share:create": "membersCanShareExternally",
  "member:invite": "membersCanInvite",
};

/**
 * The one setting that *expands* rather than restricts (§35 lists it as
 * "delete other photos: OFF", implying an admin may switch it on).
 *
 * It is kept separate from `SETTING_GATES` on purpose: the "settings can only
 * narrow" rule is what stops a group toggle from becoming a privilege-
 * escalation path, so the single exception is explicit, narrow, and cannot be
 * extended by accident. It grants exactly one content capability and nothing
 * administrative.
 */
const SETTING_GRANTS: Partial<Record<keyof GroupSettings, Capability>> = {
  membersCanDeleteOthersFiles: "file:delete_any",
};

export interface ResolveInput {
  role: GroupRole;
  groupStatus: GroupStatus;
  settings: GroupSettings;
  overrides?: PermissionOverrides | null;
}

/** Resolve the effective capability set for one member in one group. */
export function resolveCapabilities(input: ResolveInput): ReadonlySet<Capability> {
  const { role, groupStatus, settings, overrides } = input;

  if (groupStatus === "deleted" || groupStatus === "deleting") {
    return new Set();
  }

  const effective = new Set<Capability>(ROLE_CAPABILITIES[role]);

  // 1. Group policy shapes members only.
  if (role === "member") {
    for (const [capability, setting] of Object.entries(SETTING_GATES) as [
      Capability,
      keyof GroupSettings,
    ][]) {
      if (!settings[setting]) effective.delete(capability);
    }
    for (const [setting, capability] of Object.entries(SETTING_GRANTS) as [
      keyof GroupSettings,
      Capability,
    ][]) {
      if (settings[setting]) effective.add(capability);
    }
    // "Members can download" also governs the original, which additionally has
    // its own group-wide switch handled below.
    if (!settings.membersCanDownload) effective.delete("file:download_original");
  }

  // 2. Originals can be switched off group-wide, for every role.
  if (!settings.allowOriginalDownloads) {
    effective.delete("file:download_original");
  }

  // 3. Per-member overrides. Deny wins over grant when both are present.
  if (overrides?.grant) {
    for (const capability of overrides.grant) {
      if (NON_GRANTABLE.includes(capability)) continue;
      // A grant cannot resurrect a capability the group has switched off
      // globally — otherwise "allow original downloads: off" would be a lie.
      if (capability === "file:download_original" && !settings.allowOriginalDownloads) continue;
      effective.add(capability);
    }
  }
  if (overrides?.deny) {
    for (const capability of overrides.deny) effective.delete(capability);
  }

  // 4. A locked group is read-only for everyone, including its owner.
  if (groupStatus === "locked") {
    for (const capability of effective) {
      if (!READ_ONLY_CAPABILITIES.includes(capability)) effective.delete(capability);
    }
  }

  return effective;
}

export function can(capabilities: ReadonlySet<Capability>, capability: Capability): boolean {
  return capabilities.has(capability);
}

/**
 * Can `actor` delete this specific file? Ownership-aware, so the caller never
 * has to remember to combine two capabilities and get it wrong.
 */
export function canDeleteFile(
  capabilities: ReadonlySet<Capability>,
  actorUserId: string,
  fileUploaderId: string,
): boolean {
  if (capabilities.has("file:delete_any")) return true;
  return capabilities.has("file:delete_own") && actorUserId === fileUploaderId;
}

export function canDeletePost(
  capabilities: ReadonlySet<Capability>,
  actorUserId: string,
  authorId: string,
): boolean {
  if (capabilities.has("post:delete_any")) return true;
  return capabilities.has("post:delete_own") && actorUserId === authorId;
}

export function canDeleteComment(
  capabilities: ReadonlySet<Capability>,
  actorUserId: string,
  authorId: string,
): boolean {
  if (capabilities.has("comment:delete_any")) return true;
  return capabilities.has("comment:delete_own") && actorUserId === authorId;
}

const ROLE_RANK: Record<GroupRole, number> = { member: 0, admin: 1, owner: 2 };

/** Strictly-greater comparison, used for every "may act upon" decision. */
export function outranks(actor: GroupRole, target: GroupRole): boolean {
  return ROLE_RANK[actor] > ROLE_RANK[target];
}

/**
 * Role assignment rules:
 *  - you must hold `member:manage_roles`;
 *  - you must outrank the member you are changing (an admin cannot demote an
 *    owner, and admins cannot demote each other);
 *  - you cannot promote anyone to a role at or above your own (no self-cloning
 *    into owner, no privilege escalation);
 *  - only the owner may transfer ownership, which is a distinct operation.
 */
export function canAssignRole(
  capabilities: ReadonlySet<Capability>,
  actorRole: GroupRole,
  targetCurrentRole: GroupRole,
  targetNewRole: GroupRole,
): boolean {
  if (!capabilities.has("member:manage_roles")) return false;
  if (targetNewRole === "owner") return false; // ownership transfer is separate
  if (!outranks(actorRole, targetCurrentRole)) return false;
  if (!outranks(actorRole, targetNewRole)) return false;
  return true;
}

export function canRemoveMember(
  capabilities: ReadonlySet<Capability>,
  actorRole: GroupRole,
  actorUserId: string,
  targetRole: GroupRole,
  targetUserId: string,
): boolean {
  if (actorUserId === targetUserId) return true; // leaving is always allowed…
  if (!capabilities.has("member:remove")) return false;
  return outranks(actorRole, targetRole);
}

/** An invite may never mint a role at or above the creator's own. */
export function clampInviteRole(creatorRole: GroupRole, requested: GroupRole): GroupRole {
  if (requested === "owner") return "member";
  if (!outranks(creatorRole, requested)) return "member";
  return requested;
}

/* ───────────────────────── Platform (super admin) ────────────────────────── */

/**
 * Platform capabilities are a separate axis. A platform admin operates on group
 * *metadata* — quota, lock state, job health — and deliberately has NO
 * capability to view group photos. Being able to administer a service must not
 * mean being able to browse everyone's holiday pictures. To see a group's
 * content, a platform admin has to be invited into it like anyone else, which
 * leaves an audit trail.
 */
export const PLATFORM_CAPABILITIES = [
  "platform:group_read",
  "platform:group_quota",
  "platform:group_lock",
  "platform:user_read",
  "platform:user_suspend",
  "platform:jobs_read",
  "platform:jobs_retry",
  "platform:audit_read",
  "platform:stats_read",
] as const;

export type PlatformCapability = (typeof PLATFORM_CAPABILITIES)[number];

export function resolvePlatformCapabilities(isPlatformAdmin: boolean): ReadonlySet<PlatformCapability> {
  return isPlatformAdmin ? new Set(PLATFORM_CAPABILITIES) : new Set();
}

/** Operations that additionally require a fresh TOTP code (§5, §7, §100). */
export const TOTP_PROTECTED_ACTIONS = [
  "group:delete",
  "platform:group_quota",
  "platform:user_suspend",
  "totp:disable",
] as const;

export type TotpProtectedAction = (typeof TOTP_PROTECTED_ACTIONS)[number];

export function requiresTotp(action: string): action is TotpProtectedAction {
  return (TOTP_PROTECTED_ACTIONS as readonly string[]).includes(action);
}
